import * as React from "react"
import { cn } from "@/lib/utils"

export interface RadarLoaderProps extends React.SVGAttributes<SVGSVGElement> {
  size?: "sm" | "md" | "lg"
  label?: string
}

const SIZES = {
  sm: { diameter: 32, strokeWidth: 2 },
  md: { diameter: 48, strokeWidth: 3 },
  lg: { diameter: 64, strokeWidth: 4 },
}

function RadarLoader({
  size = "md",
  label,
  className,
  ...props
}: RadarLoaderProps) {
  const { diameter, strokeWidth } = SIZES[size]
  const center = diameter / 2
  const radius = (diameter - strokeWidth * 2) / 2

  return (
    <div className={cn("inline-flex flex-col items-center gap-2", className)}>
      <svg
        width={diameter}
        height={diameter}
        viewBox={`0 0 ${diameter} ${diameter}`}
        {...props}
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="hsl(var(--av-background))"
          className="stroke-aviation-border"
          strokeWidth={strokeWidth}
        />

        {/* Inner grid circles */}
        <circle
          cx={center}
          cy={center}
          r={radius * 0.66}
          fill="none"
          className="stroke-aviation-border"
          strokeWidth={1}
          opacity={0.3}
        />
        <circle
          cx={center}
          cy={center}
          r={radius * 0.33}
          fill="none"
          className="stroke-aviation-border"
          strokeWidth={1}
          opacity={0.3}
        />

        {/* Cross hairs */}
        <line
          x1={center}
          y1={strokeWidth}
          x2={center}
          y2={diameter - strokeWidth}
          className="stroke-aviation-border"
          strokeWidth={1}
          opacity={0.2}
        />
        <line
          x1={strokeWidth}
          y1={center}
          x2={diameter - strokeWidth}
          y2={center}
          className="stroke-aviation-border"
          strokeWidth={1}
          opacity={0.2}
        />

        {/* Radar sweep line */}
        <g
          className="animate-radar-sweep"
          style={{ transformOrigin: `${center}px ${center}px` }}
        >
          {/* Sweep gradient */}
          <defs>
            <linearGradient id="sweepGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="hsl(var(--av-accent))" stopOpacity="0" />
              <stop offset="100%" stopColor="hsl(var(--av-accent))" stopOpacity="0.8" />
            </linearGradient>
          </defs>

          {/* Sweep arc (trailing effect) */}
          <path
            d={`M ${center} ${center} L ${center} ${strokeWidth + 2} A ${radius - 2} ${radius - 2} 0 0 1 ${center + radius * 0.7} ${center - radius * 0.7} Z`}
            fill="url(#sweepGradient)"
            opacity={0.3}
          />

          {/* Main sweep line */}
          <line
            x1={center}
            y1={center}
            x2={center}
            y2={strokeWidth + 2}
            className="stroke-aviation-accent"
            strokeWidth={2}
            strokeLinecap="round"
            style={{
              filter: "drop-shadow(0 0 4px hsl(var(--av-accent) / 0.8))",
            }}
          />

          {/* Blip at end of sweep line */}
          <circle
            cx={center}
            cy={strokeWidth + 6}
            r={2}
            className="fill-aviation-accent"
            style={{
              filter: "drop-shadow(0 0 3px hsl(var(--av-accent)))",
            }}
          />
        </g>

        {/* Center dot */}
        <circle
          cx={center}
          cy={center}
          r={2}
          className="fill-aviation-primary"
        />
      </svg>

      {/* Label */}
      {label && (
        <span className="text-xs text-aviation-muted font-body animate-pulse">
          {label}
        </span>
      )}
    </div>
  )
}

export { RadarLoader }
